import type { CSSProperties } from 'react';

import type { ThemeContextValue } from '@course-authoring/shared-types';
import { createStylizedListStyles } from '@course-authoring/ui-blocks';

type EditorTokens = ThemeContextValue['tokens'];
type StylizedListStyles = ReturnType<typeof createStylizedListStyles>;

export interface StylizedNumberedListEditorStyles {
  container: CSSProperties;
  topToolbar: CSSProperties;
  topToolbarSection: CSSProperties;
  topToolbarLabel: CSSProperties;
  styleToggleButton: CSSProperties;
  styleToggleIcon: CSSProperties;
  styleToggleLabel: CSSProperties;
  styleToggleArrow: CSSProperties;
  titleLevelButtonGroup: CSSProperties;
  titleLevelButton: CSSProperties;
  titleLevelButtonActive: CSSProperties;
  titleInput: CSSProperties;
  itemsList: CSSProperties;
  itemContainer: CSSProperties;
  editor: CSSProperties;
  itemToolbar: CSSProperties;
  toolbarButton: CSSProperties;
  toolbarButtonActive: CSSProperties;
  toolbarSpacer: CSSProperties;
  deleteButton: CSSProperties;
  deleteButtonDisabled: CSSProperties;
  addButton: CSSProperties;
}

export const stylizedNumberedListEditorInnerStyles = `
  .snl-item-editor p {
    margin: 0;
  }
  .snl-item-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

export function createStylizedNumberedListEditorStyles(
  tokens: EditorTokens,
  listStyles: StylizedListStyles,
  isFocused: boolean
): StylizedNumberedListEditorStyles {
  const sharedTitleGap =
    typeof listStyles.container.gap === 'string'
      ? listStyles.container.gap
      : tokens.spacing.xl;

  return {
    container: {
      ...listStyles.container,
      gap: 0,
      position: 'relative',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: isFocused ? tokens.ui.selectionBorder : 'transparent',
      borderRadius: tokens.radius.md,
      transition: `border-color ${tokens.ui.transitionDefault}`,
      padding: tokens.spacing.sm,
    },
    topToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: tokens.spacing.md,
      width: '100%',
      marginBottom: tokens.spacing.lg,
    },
    topToolbarSection: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: tokens.spacing.sm,
    },
    topToolbarLabel: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
    },
    styleToggleButton: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      color: tokens.text.muted,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      fontWeight: 600,
    },
    styleToggleIcon: {
      minWidth: '16px',
      textAlign: 'center',
    },
    styleToggleLabel: {
      fontSize: '13px',
    },
    styleToggleArrow: {
      fontSize: '12px',
      opacity: 0.7,
    },
    titleLevelButtonGroup: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
      padding: tokens.spacing.xs,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      boxShadow: tokens.shadow.sm,
    },
    titleLevelButton: {
      minWidth: '36px',
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      border: 'none',
      borderRadius: tokens.radius.sm,
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
    },
    titleLevelButtonActive: {
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.primary,
      boxShadow: `inset 0 0 0 1px ${tokens.ui.selectionBorder}`,
    },
    titleInput: {
      ...listStyles.title,
      padding: `0 0 ${tokens.spacing.xs}`,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: `1px solid ${tokens.borders.default}`,
      outline: 'none',
    },
    itemsList: {
      ...listStyles.list,
      marginTop: `calc(${sharedTitleGap} - ${tokens.spacing.xs})`,
    },
    itemContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    editor: {
      paddingTop: 0,
    },
    itemToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
      paddingBottom: tokens.spacing.sm,
    },
    toolbarButton: {
      minWidth: '32px',
      height: '32px',
      padding: '0 8px',
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.muted,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: tokens.typography.fontFamily,
    },
    toolbarButtonActive: {
      backgroundColor: tokens.ui.selectionBorder,
      borderColor: tokens.ui.selectionBorder,
      color: tokens.text.onInverse,
    },
    toolbarSpacer: {
      flex: 1,
    },
    deleteButton: {
      minWidth: '32px',
      height: '32px',
      padding: '0 8px',
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.muted,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
    },
    deleteButtonDisabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
    addButton: {
      marginTop: tokens.spacing.xl,
      width: '100%',
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.muted,
      backgroundColor: 'transparent',
      border: `1px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      cursor: 'pointer',
      textAlign: 'center',
      fontFamily: tokens.typography.fontFamily,
    },
  };
}
